import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Mic, Square, Loader2, Lock, Crown, ScanLine } from "lucide-react";
import api from "@/lib/api";
import { PAYMENT_METHODS, BILLING_CYCLES } from "@/lib/format";
import { useCategories } from "@/contexts/CategoriesContext";
import { iconForCategoryName } from "@/lib/categoryIcons";
import { useAuth } from "@/contexts/AuthContext";
import { useHousehold } from "@/contexts/HouseholdContext";
import { hasProAccess } from "@/lib/plans";
import { scanReceiptImage, imageFromPaste } from "@/lib/receiptScan";
import ScanReviewDialog from "@/components/ScanReviewDialog";

function todayStr() { return new Date().toISOString().slice(0, 10); }

// Picks the best audio format MediaRecorder supports on this device — Safari/iOS only
// speaks audio/mp4, Chrome/Android and desktop prefer webm. First supported one wins.
function pickAudioMimeType() {
    if (typeof MediaRecorder === "undefined") return "";
    const candidates = ["audio/webm", "audio/mp4", "audio/aac", "audio/ogg"];
    for (const c of candidates) {
        if (MediaRecorder.isTypeSupported?.(c)) return c;
    }
    return "";
}

function extForMimeType(mimeType) {
    if (mimeType.includes("mp4") || mimeType.includes("aac")) return "m4a";
    if (mimeType.includes("ogg")) return "ogg";
    return "webm";
}

export default function QuickAddDialog({ open, onOpenChange, defaultTab = "expense", onCreated }) {
    const { categories, categoryNames } = useCategories();
    const { user } = useAuth();
    const { isMember } = useHousehold();
    // Voice entry (mic -> transcribe -> parse) is Pro/Family only — see lib/plans.js's
    // hasProAccess() and require_pro in server.py, which is the real enforcement point.
    const hasPro = hasProAccess(user, isMember);
    
    
    
    
    const [tab, setTab] = useState(defaultTab);
    const [saving, setSaving] = useState(false);

    // expense/income
    const [eForm, setEForm] = useState({
        title: "", amount: "", type: "expense", category: "Other",
        payment_method: "Credit Card", notes: "", occurred_at: todayStr(),
    });
    // subscription
    const [sForm, setSForm] = useState({
        name: "", cost: "", billing_cycle: "monthly", renewal_date: todayStr(),
        category: "Other", payment_method: "Credit Card", notes: "",
    });
    // fund
    const [fForm, setFForm] = useState({
        name: "", goal_amount: "", current_amount: "0", category: "General",
        due_date: "", notes: "",
    });

    // voice entry — "idle" | "recording" | "processing"
    const [voiceStatus, setVoiceStatus] = useState("idle");
    const [voiceError, setVoiceError] = useState("");
    const [heardText, setHeardText] = useState("");
    const [elapsed, setElapsed] = useState(0);
    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);
    const streamRef = useRef(null);
    const timerRef = useRef(null);

    // receipt / screenshot scan — Pro/Family only, same gate as voice entry
    const [scanning, setScanning] = useState(false);
    const [scanResult, setScanResult] = useState(null); // multi-row results open ScanReviewDialog
    const fileInputRef = useRef(null);

    const stopMicStream = () => {
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
    };

    // Stop any in-flight recording if the dialog gets closed mid-recording.
    useEffect(() => {
        if (!open) {
            clearInterval(timerRef.current);
            if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
            stopMicStream();
            setVoiceStatus("idle");
            setVoiceError("");
            setHeardText("");
        }
    }, [open]);

    const handleScanFile = async (file) => {
        if (!hasPro || !file || scanning) return;
        setVoiceError("");
        setHeardText("");
        setScanning(true);
        try {
            const data = await scanReceiptImage(file);
            if (data.items.length === 1) {
                // Single receipt/payment: pre-fill the normal form, same as voice entry
                const it = data.items[0];
                setEForm({
                    title: it.title,
                    amount: String(it.amount),
                    type: it.type,
                    category: it.category,
                    payment_method: it.payment_method || "Credit Card",
                    notes: it.notes || "",
                    occurred_at: it.occurred_at,
                });
                setTab(it.type === "income" ? "income" : "expense");
                if (it.currency && it.currency !== (user?.currency || "USD")) {
                    toast.warning(`That looks like ${it.currency} — your app currency is ${user?.currency || "USD"}`);
                } else {
                    toast.success("Read it — review the details below and save");
                }
            } else {
                // Statement / many rows: hand off to the review dialog
                onOpenChange(false);
                setScanResult(data);
            }
        } catch (e) {
            setVoiceError(e.message);
        } finally {
            setScanning(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onPaste = (e) => {
        if (!hasPro) return;
        const file = imageFromPaste(e);
        if (file) { e.preventDefault(); handleScanFile(file); }
    };

    const startVoiceEntry = async () => {
        if (!hasPro) return; // button is hidden for free users — this is just defense in depth
        setVoiceError("");
        setHeardText("");
        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
            setVoiceError("Voice entry isn't supported in this browser — try Chrome or Safari.");
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const mimeType = pickAudioMimeType();
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            chunksRef.current = [];
            recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
            recorder.onstop = () => handleRecordingStop(mimeType || recorder.mimeType || "audio/webm");
            mediaRecorderRef.current = recorder;
            recorder.start();
            setVoiceStatus("recording");
            setElapsed(0);
            timerRef.current = setInterval(() => setElapsed(s => s + 1), 1000);
        } catch (err) {
            setVoiceError("Microphone access was denied. Check your browser's site permissions.");
        }
    };

    const stopVoiceEntry = () => {
        clearInterval(timerRef.current);
        if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
        stopMicStream();
    };

    const handleRecordingStop = async (mimeType) => {
        setVoiceStatus("processing");
        const blob = new Blob(chunksRef.current, { type: mimeType });
        if (blob.size === 0) {
            setVoiceStatus("idle");
            setVoiceError("Didn't catch anything — try again.");
            return;
        }
        const formData = new FormData();
        formData.append("audio", blob, `voice-entry.${extForMimeType(mimeType)}`);
        try {
            const { data } = await api.post("/voice/parse-expense", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setHeardText(data.transcript);
            setEForm({
                title: data.title,
                amount: String(data.amount),
                type: data.type,
                category: data.category,
                payment_method: data.payment_method || "Credit Card",
                notes: data.notes || "",
                occurred_at: data.occurred_at,
            });
            setTab(data.type === "income" ? "income" : "expense");
            toast.success("Got it — review the details below and save");
        } catch (e) {
            setVoiceError(e.response?.data?.detail || "Couldn't process that recording. Try again or enter it manually.");
        } finally {
            setVoiceStatus("idle");
        }
    };

    const close = () => onOpenChange(false);

    const submitExpense = async (type) => {
        if (!eForm.title || !eForm.amount) { toast.error("Title and amount are required"); return; }
        try {
            setSaving(true);
            const payload = {
                ...eForm, type, amount: parseFloat(eForm.amount),
                occurred_at: new Date(eForm.occurred_at).toISOString(),
            };
            await api.post("/expenses", payload);
            toast.success(`${type === "income" ? "Income" : "Expense"} added`);
            onCreated?.();
            close();
            setEForm({ ...eForm, title: "", amount: "", notes: "" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    const submitSub = async () => {
        if (!sForm.name || !sForm.cost) { toast.error("Name and cost are required"); return; }
        try {
            setSaving(true);
            await api.post("/subscriptions", { ...sForm, cost: parseFloat(sForm.cost) });
            toast.success("Subscription added");
            onCreated?.(); close();
            setSForm({ ...sForm, name: "", cost: "", notes: "" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    const submitFund = async () => {
        if (!fForm.name || !fForm.goal_amount) { toast.error("Name and goal are required"); return; }
        try {
            setSaving(true);
            const payload = {
                ...fForm,
                goal_amount: parseFloat(fForm.goal_amount),
                current_amount: parseFloat(fForm.current_amount || 0),
                due_date: fForm.due_date || null,
            };
            await api.post("/funds", payload);
            toast.success("Sinking fund created");
            onCreated?.(); close();
            setFForm({ ...fForm, name: "", goal_amount: "", current_amount: "0", notes: "" });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to save");
        } finally { setSaving(false); }
    };

    return (
        <>
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="quick-add-dialog" className="sm:max-w-lg rounded-2xl" onPaste={onPaste}>
                <DialogHeader>
                    <DialogTitle className="font-display text-xl">Quick add</DialogTitle>
                </DialogHeader>

                {!hasPro ? (
                    <div className="rounded-xl border border-dashed border-amber-200 bg-amber-50/60 px-3 py-2.5" data-testid="voice-entry-bar">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 text-xs text-slate-600">
                                <Lock className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                <span><span className="font-semibold text-slate-800">Voice entry & receipt scan</span> are Pro features</span>
                            </div>
                            <Link
                                to="/pricing" onClick={close} data-testid="voice-upsell-link"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white text-xs font-semibold shrink-0 shadow-sm shadow-amber-500/20"
                            >
                                <Crown className="w-3.5 h-3.5" /> Upgrade
                            </Link>
                        </div>
                    </div>
                ) : (
                    <div className="rounded-xl border border-dashed border-indigo-200 bg-indigo-50/50 px-3 py-2.5 space-y-2" data-testid="voice-entry-bar">
                        <input
                            ref={fileInputRef} type="file" accept="image/*" className="hidden" data-testid="scan-file-input"
                            onChange={e => handleScanFile(e.target.files?.[0])}
                        />
                        {voiceStatus === "idle" && !scanning && (
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-xs text-slate-600 min-w-0">
                                    <span className="font-semibold text-slate-800">Speak it, or scan a receipt</span>
                                    <div className="text-[11px] text-slate-500 truncate">Photo, screenshot or statement — or paste one here</div>
                                </div>
                                <div className="flex gap-1.5 shrink-0">
                                    <Button
                                        type="button" size="sm" variant="outline" data-testid="scan-start"
                                        onClick={() => fileInputRef.current?.click()}
                                        className="gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                                    >
                                        <ScanLine className="w-3.5 h-3.5" /> Scan
                                    </Button>
                                    <Button
                                        type="button" size="sm" variant="outline" data-testid="voice-start"
                                        onClick={startVoiceEntry}
                                        className="gap-1.5 border-indigo-300 text-indigo-700 hover:bg-indigo-100"
                                    >
                                        <Mic className="w-3.5 h-3.5" /> Speak
                                    </Button>
                                </div>
                            </div>
                        )}
                        {scanning && (
                            <div className="flex items-center gap-2 text-xs font-medium text-indigo-700" data-testid="scan-processing">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Reading your image…
                            </div>
                        )}
                        {voiceStatus === "recording" && (
                            <div className="flex items-center justify-between gap-2">
                                <div className="flex items-center gap-2 text-xs font-medium text-indigo-700">
                                    <span className="relative flex h-2.5 w-2.5">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
                                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500" />
                                    </span>
                                    Listening… {elapsed}s
                                </div>
                                <Button
                                    type="button" size="sm" data-testid="voice-stop"
                                    onClick={stopVoiceEntry}
                                    className="gap-1.5 shrink-0 bg-rose-600 hover:bg-rose-700 text-white"
                                >
                                    <Square className="w-3 h-3" /> Stop
                                </Button>
                            </div>
                        )}
                        {voiceStatus === "processing" && (
                            <div className="flex items-center gap-2 text-xs font-medium text-indigo-700">
                                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Understanding what you said…
                            </div>
                        )}
                        {heardText && voiceStatus === "idle" && (
                            <p className="text-[11px] text-slate-500 italic" data-testid="voice-transcript">Heard: "{heardText}"</p>
                        )}
                        {voiceError && (
                            <p className="text-[11px] text-rose-600" data-testid="voice-error">{voiceError}</p>
                        )}
                    </div>
                )}

                <Tabs value={tab} onValueChange={setTab}>
                    <TabsList className="grid grid-cols-4 w-full" data-testid="quick-add-tabs">
                        <TabsTrigger value="expense" data-testid="tab-expense">Expense</TabsTrigger>
                        <TabsTrigger value="income" data-testid="tab-income">Income</TabsTrigger>
                        <TabsTrigger value="subscription" data-testid="tab-subscription">Subscription</TabsTrigger>
                        <TabsTrigger value="fund" data-testid="tab-fund">Fund</TabsTrigger>
                    </TabsList>

                    {/* Expense */}
                    <TabsContent value="expense" className="space-y-3 pt-3">
                        <Field label="Title">
                            <Input data-testid="exp-title" value={eForm.title} onChange={e => setEForm({...eForm, title: e.target.value})} placeholder="Groceries at Whole Foods" />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Amount">
                                <Input data-testid="exp-amount" type="number" step="0.01" value={eForm.amount} onChange={e => setEForm({...eForm, amount: e.target.value})} placeholder="0.00" />
                            </Field>
                            <Field label="Date">
                                <Input data-testid="exp-date" type="date" value={eForm.occurred_at} onChange={e => setEForm({...eForm, occurred_at: e.target.value})} />
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Category">
                                <Select value={eForm.category} onValueChange={v => setEForm({...eForm, category: v})}>
                                    <SelectTrigger data-testid="exp-category"><SelectValue /></SelectTrigger>
                                    <SelectContent><CategoryOptions categories={categories} categoryNames={categoryNames} /></SelectContent>
                                </Select>
                            </Field>
                            <Field label="Payment">
                                <Select value={eForm.payment_method} onValueChange={v => setEForm({...eForm, payment_method: v})}>
                                    <SelectTrigger data-testid="exp-payment"><SelectValue /></SelectTrigger>
                                    <SelectContent>{PAYMENT_METHODS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                </Select>
                            </Field>
                        </div>
                        <Field label="Notes">
                            <Textarea data-testid="exp-notes" rows={2} value={eForm.notes} onChange={e => setEForm({...eForm, notes: e.target.value})} placeholder="Optional notes" />
                        </Field>
                        <DialogFooter>
                            <Button variant="ghost" onClick={close}>Cancel</Button>
                            <Button data-testid="exp-save" disabled={saving} onClick={() => submitExpense("expense")} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                Save expense
                            </Button>
                        </DialogFooter>
                    </TabsContent>

                    {/* Income */}
                    <TabsContent value="income" className="space-y-3 pt-3">
                        <Field label="Source">
                            <Input data-testid="inc-title" value={eForm.title} onChange={e => setEForm({...eForm, title: e.target.value})} placeholder="January salary" />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Amount">
                                <Input data-testid="inc-amount" type="number" step="0.01" value={eForm.amount} onChange={e => setEForm({...eForm, amount: e.target.value})} placeholder="0.00" />
                            </Field>
                            <Field label="Date">
                                <Input data-testid="inc-date" type="date" value={eForm.occurred_at} onChange={e => setEForm({...eForm, occurred_at: e.target.value})} />
                            </Field>
                        </div>
                        <Field label="Category">
                            <Select value={categoryNames.includes(eForm.category) ? eForm.category : "Salary"} onValueChange={v => setEForm({...eForm, category: v})}>
                                <SelectTrigger data-testid="inc-category"><SelectValue /></SelectTrigger>
                                <SelectContent><CategoryOptions categories={categories} categoryNames={categoryNames} /></SelectContent>
                            </Select>
                        </Field>
                        <DialogFooter>
                            <Button variant="ghost" onClick={close}>Cancel</Button>
                            <Button data-testid="inc-save" disabled={saving} onClick={() => submitExpense("income")} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                                Save income
                            </Button>
                        </DialogFooter>
                    </TabsContent>

                    {/* Subscription */}
                    <TabsContent value="subscription" className="space-y-3 pt-3">
                        <Field label="Name">
                            <Input data-testid="sub-name" value={sForm.name} onChange={e => setSForm({...sForm, name: e.target.value})} placeholder="Netflix" />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Cost">
                                <Input data-testid="sub-cost" type="number" step="0.01" value={sForm.cost} onChange={e => setSForm({...sForm, cost: e.target.value})} placeholder="0.00" />
                            </Field>
                            <Field label="Billing cycle">
                                <Select value={sForm.billing_cycle} onValueChange={v => setSForm({...sForm, billing_cycle: v})}>
                                    <SelectTrigger data-testid="sub-cycle"><SelectValue /></SelectTrigger>
                                    <SelectContent>{BILLING_CYCLES.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Renewal date">
                                <Input data-testid="sub-renewal" type="date" value={sForm.renewal_date} onChange={e => setSForm({...sForm, renewal_date: e.target.value})} />
                            </Field>
                            <Field label="Category">
                                <Select value={sForm.category} onValueChange={v => setSForm({...sForm, category: v})}>
                                    <SelectTrigger data-testid="sub-category"><SelectValue /></SelectTrigger>
                                    <SelectContent><CategoryOptions categories={categories} categoryNames={categoryNames} /></SelectContent>
                                </Select>
                            </Field>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={close}>Cancel</Button>
                            <Button data-testid="sub-save" disabled={saving} onClick={submitSub} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                                Save subscription
                            </Button>
                        </DialogFooter>
                    </TabsContent>

                    {/* Fund */}
                    <TabsContent value="fund" className="space-y-3 pt-3">
                        <Field label="Goal name">
                            <Input data-testid="fund-name" value={fForm.name} onChange={e => setFForm({...fForm, name: e.target.value})} placeholder="Japan vacation" />
                        </Field>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Goal amount">
                                <Input data-testid="fund-goal" type="number" step="0.01" value={fForm.goal_amount} onChange={e => setFForm({...fForm, goal_amount: e.target.value})} placeholder="5000" />
                            </Field>
                            <Field label="Already saved">
                                <Input data-testid="fund-current" type="number" step="0.01" value={fForm.current_amount} onChange={e => setFForm({...fForm, current_amount: e.target.value})} placeholder="0" />
                            </Field>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="Category">
                                <Select value={fForm.category || "General"} onValueChange={v => setFForm({...fForm, category: v})}>
                                    <SelectTrigger data-testid="fund-category"><SelectValue /></SelectTrigger>
                                    <SelectContent><CategoryOptions categories={categories} categoryNames={categoryNames} /></SelectContent>
                                </Select>
                            </Field>
                            <Field label="Due date (optional)">
                                <Input data-testid="fund-due" type="date" value={fForm.due_date} onChange={e => setFForm({...fForm, due_date: e.target.value})} />
                            </Field>
                        </div>
                        <DialogFooter>
                            <Button variant="ghost" onClick={close}>Cancel</Button>
                            <Button data-testid="fund-save" disabled={saving} onClick={submitFund} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                                Create fund
                            </Button>
                        </DialogFooter>
                    </TabsContent>
                </Tabs>
            </DialogContent>
        </Dialog>
        <ScanReviewDialog
            scan={scanResult}
            onOpenChange={v => { if (!v) setScanResult(null); }}
            onSaved={() => onCreated?.()}
        />
        </>
    );
}

function Field({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-slate-700">{label}</Label>
            {children}
        </div>
    );
}

function CategoryOptions({ categories, categoryNames }) {
    return categoryNames.map(c => {
        const { icon: CatIcon, color } = iconForCategoryName(categories, c);
        return (
            <SelectItem key={c} value={c}>
                <span className="inline-flex items-center gap-2"><CatIcon className="w-3.5 h-3.5" style={{ color }} />{c}</span>
            </SelectItem>
        );
    });
}
